/**
 * @lines98/client/components/icon-button — примитив «иконка-кнопка».
 *
 * ЗАЧЕМ (owner-directive 2026-06-04 «делаем примитивы»). Квадратные иконки-команды
 * (× закрыть, ⚙ настройки, ‹ назад, ‹/›/⏯ реплей-транспорт) раньше стилизовались
 * на каждом экране врозь → расходились: разные рамки, разное центрирование глифа
 * (текстовый «×» уезжал вниз из-за наследованного line-height базовой .btn). Это
 * единый кирпичик: правила (тактильные 44px, идеальное центрирование, единый вид,
 * доступность) зашиты ВНУТРЬ — экран не задаёт размер/центровку руками, их негде
 * сломать. Модель «ствол → ветки»: экран монтирует кирпичик, не копирует логику.
 *
 * Два варианта (ОДИН источник правила 44px+центрирование для ВСЕХ иконок-кнопок):
 *   - ghost (default) — frameless, круглый: тихие утилиты (× закрыть, ⚙ настройки),
 *     канон (owner-confirmed 2026-06-04 «× норм»);
 *   - framed — рамка + фон + тень, скруглённый квадрат: действия/плавающие (☰ бургер),
 *     где нужен контраст/аффорданс. Примитив поддерживает ОБА вида, не уплощает один.
 * Размеры — токенами (--btn-h / --fs-control), без хардкода.
 *
 * Глиф: SVG-разметка (предпочтительно — чёткое геометрическое центрирование, как
 * ICON_* в других компонентах) ИЛИ текстовый символ. Передаётся как innerHTML.
 *
 * CSS — co-located `icon-button.css`.
 */

import './icon-button.css';

export interface IconButtonOptions {
  /** Глиф: SVG-разметка (рекомендуется) или текстовый символ. Идёт в innerHTML. */
  readonly glyph: string;
  /** Доступное имя (aria-label) — обязательно: кнопка без текстовой подписи. */
  readonly label: string;
  /** Обработчик клика (опционально — можно навесить и снаружи на .element). */
  readonly onClick?: (e: MouseEvent) => void;
  /** Вид: 'ghost' (default, frameless круглый) | 'framed' (рамка+фон+тень). */
  readonly variant?: 'ghost' | 'framed';
  /** Доп. класс(ы) для позиционирования/контекста экрана (вид не меняют). */
  readonly className?: string;
  /** type кнопки (по умолчанию 'button' — не сабмитит формы). */
  readonly type?: 'button' | 'submit';
}

export interface IconButtonHandle {
  /** Корневой <button> — потребитель вставляет в разметку экрана. */
  readonly element: HTMLButtonElement;
}

/** Создать иконку-кнопку. Возвращает handle с `.element`. */
export function createIconButton(opts: IconButtonOptions): IconButtonHandle {
  const btn = document.createElement('button');
  btn.type = opts.type ?? 'button';
  btn.classList.add('icon-btn');
  if (opts.variant === 'framed') btn.classList.add('icon-btn--framed');
  if (opts.className) btn.classList.add(...opts.className.split(/\s+/).filter(Boolean));
  btn.setAttribute('aria-label', opts.label);
  btn.innerHTML = opts.glyph;
  if (opts.onClick) btn.addEventListener('click', opts.onClick);
  return { element: btn };
}

/** SVG-крестик «закрыть» — две диагонали; чёткое центрирование (не текстовый «×»). */
export const ICON_CLOSE = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" /></svg>`;

/**
 * SVG-дискета «Сохранить в Книгу» (Tabler «device-floppy», outline) — ЕДИНЫЙ источник
 * глифа дискеты (рядом с ICON_CLOSE), НЕ текстовая «💾» (AC-B2/B10, §10.3 glyph-канон:
 * геометрическое центрирование SVG, не зависящее от шрифт-метрик эмодзи). Внутри
 * viewBox 0..24 (x∈[4,20]) → stroke не упирается в край. Потребитель — `lib/replay-
 * save-button` (kit дискеты на чужом реплее). Состояние «в Книге» помечается классом
 * на самой кнопке (.replay-save-btn[data-saved]) — ОДИН visual marker (AC-B3), не
 * второй глиф: «галка-в-дискете» рисуется CSS-наложением, не сменой SVG.
 */
export const ICON_SAVE = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2" /><path d="M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M14 4l0 4l-6 0l0 -4" /></svg>`;

/**
 * SVG-ПЕРЕЧЁРКНУТАЯ дискета «убрать из архива / un-save» (Tabler «device-floppy-off»,
 * outline) — ПАРНЫЙ к ICON_SAVE глиф снятия записи (один реестр, рядом с дискетой).
 * ЗАЧЕМ (owner-directive 2026-06-25, тикет «Архив·Чужие·удаление»): кнопка-удаления
 * строки чужого реплея в Архиве больше НЕ безымянный крестик «✕» — это семантически
 * «убрать из архива» = дискета + диагональная перечёркивающая линия (антоним «Сохранить»),
 * консистентно со стилем диск-кнопки `replay-save-button`. Геометрия — та же дискета
 * (контур floppy), плюс slash 3→21 (как у других «-off» Tabler-глифов) + диагональный
 * вырез линий, чтобы перечёркивание читалось поверх. viewBox 0..24, stroke не у края.
 * Потребитель — book-screen (кнопка `.book-row-remove` у «Чужих»).
 */
export const ICON_SAVE_OFF = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 4h6l8 8v6m-2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2" /><path d="M12.5 11.5a2 2 0 1 0 2 2" /><path d="M14 4v4h-6" /><path d="M3 3l18 18" /></svg>`;

/**
 * SVG-«поделиться» (Tabler «share», outline) — ЕДИНЫЙ источник глифа шаринга (рядом с
 * ICON_SAVE/ICON_CLOSE), НЕ эмодзи «📤/🔗» (AC-11, §10.3 glyph-канон: геометрическое
 * центрирование SVG, не зависящее от шрифт-метрик эмодзи). Три узла, соединённые
 * линиями (классический share). Потребитель — `lib/replay-save-button` (share-ветка
 * кнопки плеера на СВОЁМ реплее: тап → copyShareLink('/r/<short_id>')). Внутри viewBox
 * 0..24 (x,y∈[3,21]) → stroke не упирается в край.
 */
export const ICON_SHARE = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M18 6m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M18 18m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M8.7 10.7l6.6 -3.4" /><path d="M8.7 13.3l6.6 3.4" /></svg>`;

/**
 * SVG-галочка «успех» (Tabler «check», outline) — ЕДИНЫЙ источник глифа подтверждения
 * (рядом с ICON_SHARE/ICON_SAVE/ICON_CLOSE), НЕ текстовая «✓» (§10.3 glyph-канон:
 * геометрическое центрирование SVG, не зависящее от шрифт-метрик). Потребитель —
 * `lib/replay-save-button` (success-морф share-кнопки: best-practice copy-кнопок —
 * кратковременный морф глифа в «✓» как подтверждение нажатия, owner-directive
 * 2026-06-24 «кнопка должна реагировать на нажатие»). Внутри viewBox 0..24.
 */
export const ICON_CHECK = `<svg class="icon-btn-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12l5 5l9 -9" /></svg>`;
